import { useCallback, useEffect, useRef } from 'react';
import * as u from '@virtuoso.dev/urx';
import { useRcPortalWindowContext } from '../../../../foundation';
import { correctItemSize } from '../utils/correctItemSize';
function approximatelyEqual(num1, num2) {
    return Math.abs(num1 - num2) < 1.01;
}
export default function useScrollTop(scrollContainerStateCallback, smoothScrollTargetReached, scrollerElement, scrollerRefCallback, customScrollParent) {
    if (scrollerRefCallback === void 0) { scrollerRefCallback = u.noop; }
    var scrollerRef = useRef(null);
    var scrollTopTarget = useRef(null);
    var timeoutRef = useRef(null);
    var _a = useRcPortalWindowContext().externalWindow, externalWindow = _a === void 0 ? window : _a;
    var handler = useCallback(function (ev) {
        var el = ev.target;
        var scrollTop = el === externalWindow ||
            el === externalWindow.document
            ? externalWindow.pageYOffset ||
                externalWindow.document.documentElement.scrollTop
            : el.scrollTop;
        var scrollHeight = el === externalWindow
            ? externalWindow.document.documentElement.scrollHeight
            : el.scrollHeight;
        var viewportHeight = el === externalWindow
            ? externalWindow.innerHeight
            : el.offsetHeight;
        scrollContainerStateCallback({
            scrollTop: Math.max(scrollTop, 0),
            scrollHeight: scrollHeight,
            viewportHeight: viewportHeight,
        });
        if (scrollTopTarget.current !== null) {
            if (scrollTop === scrollTopTarget.current ||
                scrollTop <= 0 ||
                scrollTop === el.scrollHeight - correctItemSize(el, 'height')) {
                scrollTopTarget.current = null;
                smoothScrollTargetReached(true);
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
            }
        }
    }, [externalWindow, scrollContainerStateCallback, smoothScrollTargetReached]);
    useEffect(function () {
        var localRef = customScrollParent
            ? customScrollParent
            : scrollerRef.current;
        scrollerRefCallback(customScrollParent ? customScrollParent : scrollerRef.current);
        handler({ target: localRef });
        localRef.addEventListener('scroll', handler, { passive: true });
        return function () {
            scrollerRefCallback(null);
            localRef.removeEventListener('scroll', handler);
        };
    }, [
        scrollerRef,
        handler,
        scrollerElement,
        scrollerRefCallback,
        customScrollParent,
    ]);
    function scrollToCallback(location) {
        var scrollerElement = scrollerRef.current;
        if (!scrollerElement ||
            ('offsetHeight' in scrollerElement && scrollerElement.offsetHeight === 0)) {
            return;
        }
        var isSmooth = location.behavior === 'smooth';
        var offsetHeight;
        var scrollHeight;
        var scrollTop;
        if (scrollerElement === externalWindow) {
            // this is not a mistake
            scrollHeight = Math.max(correctItemSize(externalWindow.document.documentElement, 'height'), externalWindow.document.documentElement.scrollHeight);
            offsetHeight = externalWindow.innerHeight;
            scrollTop = externalWindow.document.documentElement.scrollTop;
        }
        else {
            scrollHeight = scrollerElement.scrollHeight;
            offsetHeight = correctItemSize(scrollerElement, 'height');
            scrollTop = scrollerElement.scrollTop;
        }
        var maxScrollTop = scrollHeight - offsetHeight;
        location.top = Math.ceil(Math.max(Math.min(maxScrollTop, location.top), 0));
        // avoid system hanging because the DOM never called back
        // with the scrollTop
        // scroller is already at this location
        if (approximatelyEqual(offsetHeight, scrollHeight) ||
            location.top === scrollTop) {
            scrollContainerStateCallback({
                scrollTop: scrollTop,
                scrollHeight: scrollHeight,
                viewportHeight: offsetHeight,
            });
            if (isSmooth) {
                smoothScrollTargetReached(true);
            }
            return;
        }
        if (isSmooth) {
            scrollTopTarget.current = location.top;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(function () {
                timeoutRef.current = null;
                scrollTopTarget.current = null;
                smoothScrollTargetReached(true);
            }, 1000);
        }
        else {
            scrollTopTarget.current = null;
        }
        scrollerElement.scrollTo(location);
    }
    function scrollByCallback(location) {
        scrollerRef.current.scrollBy(location);
    }
    return { scrollerRef: scrollerRef, scrollByCallback: scrollByCallback, scrollToCallback: scrollToCallback };
}
